import {
  ApolloCache,
  ApolloClient,
  DefaultContext,
  MutationOptions,
  NormalizedCacheObject,
} from "@apollo/client";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { assertPresence } from "utils/assertions";
import { DocumentType, gql } from "__generated__/gql";
import { BrandDocumentInput } from "__generated__/gql/graphql";
import type { SignedPutUrlMetadata } from "./signedPutUrl";
import DocumentFileUpload, { DocumentData } from "./DocumentFileUpload";
import SignedPutUrlForBrandFileUpload from "./SignedPutUrlForBrandFileUpload";

const mutation = gql(/* GraphQL */ `
  mutation CreateBrandDocument($data: BrandDocumentInput!) {
    createBrandDocument(input: { brandDocument: $data }) {
      brandDocument {
        id
        brandId
        originalFilename
        mimeType
        fileSize
        documentFolderId
        isImport
        isArchived
        createdAt
        updatedAt
      }
    }
  }
`);

export type AdditionalBrandDocumentData = Partial<
  Omit<
    BrandDocumentInput,
    "brandId" | "file" | "fileSize" | "mimeType" | "originalFilename"
  >
>;

type MutationDoc = typeof mutation;
type MutationData = DocumentType<MutationDoc>;
type MutationVariables = DocumentVariablesType<MutationDoc>;

export type CreatedBrandDocument = NonNullable<
  NonNullable<MutationData["createBrandDocument"]>["brandDocument"]
>;

export type CreateBrandDocumentOptions = Partial<
  Omit<
    MutationOptions<
      MutationData,
      MutationVariables,
      DefaultContext,
      ApolloCache<NormalizedCacheObject>
    >,
    "variables"
  >
>;

type BrandDocumentData = BrandDocumentInput & { id: UUID };

type PerformCreateContext = {
  apolloClient: ApolloClient<unknown>;
  mutationOptions?: CreateBrandDocumentOptions;
};

type PerformCreate<R> = (
  brandDocumentData: BrandDocumentData,
  context: PerformCreateContext,
) => Promise<R>;

async function defaultPerformCreate(
  brandDocumentData: BrandDocumentData,
  { apolloClient, mutationOptions }: PerformCreateContext,
) {
  const { data } = await apolloClient.mutate({
    mutation,
    variables: { data: brandDocumentData },
    ...mutationOptions,
  });

  assertPresence(
    data?.createBrandDocument?.brandDocument?.id,
    "Could not get brand document ID from response!",
  );

  return data.createBrandDocument.brandDocument;
}

export type BrandDocumentFileUploadOptions<R = CreatedBrandDocument> = {
  additionalBrandDocumentData?: AdditionalBrandDocumentData;
  apolloClient: ApolloClient<object>;
  brandId: UUID;
  createBrandDocumentOptions?: CreateBrandDocumentOptions;
  file: File;
  filePath?: string;
  id?: string;
  metadata?: SignedPutUrlMetadata;
  performCreate?: PerformCreate<R>;
};

export default class BrandDocumentFileUpload<
  R = CreatedBrandDocument,
> extends DocumentFileUpload<R> {
  protected options: BrandDocumentFileUploadOptions<R>;

  constructor(options: BrandDocumentFileUploadOptions<R>) {
    const {
      additionalBrandDocumentData,
      apolloClient,
      brandId,
      createBrandDocumentOptions,
      file,
      filePath = "documents/",
      id,
      metadata,
      performCreate:
        performBrandDocumentCreate = defaultPerformCreate as PerformCreate<R>,
    } = options;

    const allMetadata = { ...metadata, "brand-id": brandId };

    const fileUpload = new SignedPutUrlForBrandFileUpload({
      apolloClient,
      brandId,
      file,
      filePath,
      id,
      metadata: allMetadata,
    });

    const performCreate = (data: DocumentData) =>
      performBrandDocumentCreate(
        {
          ...data,
          ...additionalBrandDocumentData,
          brandId,
          id: additionalBrandDocumentData?.id || data.id,
        },
        { apolloClient, mutationOptions: createBrandDocumentOptions },
      );

    super(fileUpload, performCreate);

    this.options = options;
  }

  getSentryExtra() {
    const base = super.getSentryExtra();
    return {
      ...base,
      additionalBrandDocumentData: this.options.additionalBrandDocumentData,
      brandId: this.options.brandId,
      filePath: this.options.filePath,
      metadata: this.options.metadata,
    };
  }
}
